/**
 * ชิ้นส่วนหน้าจอที่ใช้ซ้ำ
 *
 * เก็บไว้ที่เดียวเพราะสิ่งที่ต้องเหมือนกันทั้งแอปคือ "ขนาดที่นิ้วกดโดน"
 * ถ้าปล่อยให้แต่ละหน้าทำปุ่มเอง ขนาดจะเริ่มเพี้ยนทีละนิดจนมีปุ่มที่กดยาก
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { baht, parseBaht } from './lib/format.ts';

/* ══ ข้อความแจ้งชั่วคราว ═══════════════════════════════════ */

type ToastTone = 'plain' | 'ok' | 'warn' | 'crit';
interface Toast { id: number; text: string; tone: ToastTone; }

const ToastContext = createContext<(text: string, tone?: ToastTone) => void>(() => {});

export function ToastHost({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const nextId = useRef(1);

  const show = useCallback((text: string, tone: ToastTone = 'plain') => {
    const id = nextId.current++;
    setToasts((list) => [...list, { id, text, tone }]);
    // ข้อความที่เป็นความผิดพลาดอยู่นานกว่า เพราะพนักงานต้องอ่านให้จบก่อนตัดสินใจ
    setTimeout(() => {
      setToasts((list) => list.filter((t) => t.id !== id));
    }, tone === 'crit' ? 7000 : 3500);
  }, []);

  return (
    <ToastContext.Provider value={show}>
      {children}
      {toasts.length > 0 && (
        <div className="toast-stack">
          {toasts.map((t) => (
            <div key={t.id} className={`toast ${t.tone}`} role="status">{t.text}</div>
          ))}
        </div>
      )}
    </ToastContext.Provider>
  );
}

export const useToast = () => useContext(ToastContext);

/* ══ กล่องซ้อน ═════════════════════════════════════════════ */

export function Modal({
  title,
  onClose,
  children,
  wide = false,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
  wide?: boolean;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      className="overlay"
      onPointerDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className={`modal${wide ? ' wide' : ''}`} role="dialog" aria-label={title}>
        <h2>{title}</h2>
        {children}
      </div>
    </div>
  );
}

/* ══ แผงตัวเลข ═════════════════════════════════════════════ */

/**
 * ★ แป้นตัวเลขบนจอ ไม่ใช่คีย์บอร์ดของระบบ ★
 *
 * เครื่อง POS หน้าร้านไม่มีคีย์บอร์ดต่อไว้ และแป้นพิมพ์บนจอของ Android
 * กินพื้นที่ครึ่งจอจนมองไม่เห็นยอดที่กำลังกรอก
 *
 * ค่าที่เก็บเป็น "สตางค์" เสมอ ตัวเลขที่กดคือหลักบาท
 */
export function NumPad({
  onChange,
  onEnter,
}: {
  /** ★ รับเป็นฟังก์ชันแปลงค่า ไม่ใช่ค่าใหม่ตรง ๆ ★ ดูเหตุผลด้านล่าง */
  onChange: (update: (previous: string) => string) => void;
  onEnter?: () => void;
}) {
  /**
   * ★ คิดค่าใหม่จากค่าก่อนหน้าที่ React ถืออยู่จริง ไม่ใช่จาก prop ★
   *
   * ถ้าอ่านค่าปัจจุบันจาก prop แล้วสองการกดถูกรวมเป็นรอบเรนเดอร์เดียว
   * การกดครั้งที่สองจะเห็นค่าเก่า แล้วตัวเลขหนึ่งตัวหายไปเงียบ ๆ
   * — ยอดที่กรอกผิดหนึ่งหลักคือยอดที่ทอนเงินผิด
   */
  const press = (key: string) =>
    onChange((value) => {
      if (key === 'back') return value.slice(0, -1);
      if (key === 'clear') return '';
      if (key === '.') return value.includes('.') ? value : (value || '0') + '.';
      // กันไม่ให้พิมพ์สตางค์เกินสองตำแหน่ง
      const [, fraction = ''] = value.split('.');
      if (value.includes('.') && fraction.length >= 2) return value;
      return value === '0' ? key : value + key;
    });

  return (
    <div className="numpad">
      {['7', '8', '9', '4', '5', '6', '1', '2', '3'].map((k) => (
        <button key={k} type="button" onClick={() => press(k)}>{k}</button>
      ))}
      <button type="button" onClick={() => press('.')}>.</button>
      <button type="button" onClick={() => press('0')}>0</button>
      <button type="button" className="erase" onClick={() => press('back')}>ลบ</button>
      <button type="button" className="erase" onClick={() => press('clear')}>ล้าง</button>
      {onEnter && (
        <button type="button" className="wide erase" onClick={onEnter}>ตกลง</button>
      )}
    </div>
  );
}

/** แปลงข้อความบนแป้นเป็นสตางค์ — คืน null ถ้ายังกรอกไม่ครบ */
export function padToSatang(text: string): number | null {
  if (text === '' || text === '.') return null;
  return parseBaht(text);
}

/* ══ ปุ่มเงินสดที่กดบ่อย ═══════════════════════════════════ */

/**
 * ปุ่มธนบัตรที่ลูกค้ายื่นมาบ่อยที่สุด — คำนวณจากยอดบิลจริง
 * ยอด 142 บาท ปุ่มที่ควรมีคือ 142 (พอดี) · 200 · 500 · 1000
 */
export function quickCashOptions(totalSatang: number): number[] {
  const notes = [2000, 5000, 10000, 20000, 50000, 100000];
  const options = new Set<number>([totalSatang]);

  // ธนบัตรใบเดียวที่จ่ายได้
  for (const note of notes) if (note >= totalSatang) options.add(note);

  // ปัดขึ้นเป็นหลักร้อยและหลักพัน — ลูกค้ามักยื่นแบบนี้เมื่อยอดมีเศษ
  const hundred = Math.ceil(totalSatang / 10000) * 10000;
  const fiveHundred = Math.ceil(totalSatang / 50000) * 50000;
  if (hundred > totalSatang) options.add(hundred);
  if (fiveHundred > totalSatang) options.add(fiveHundred);

  return [...options].sort((a, b) => a - b).slice(0, 8);
}

export function QuickCash({
  totalSatang,
  onPick,
}: {
  totalSatang: number;
  onPick: (satang: number) => void;
}) {
  const options = useMemo(() => quickCashOptions(totalSatang), [totalSatang]);
  return (
    <div className="quick-cash">
      {options.map((satang) => (
        <button key={satang} type="button" onClick={() => onPick(satang)}>
          {satang === totalSatang ? 'พอดี' : baht(satang).replace('.00', '')}
        </button>
      ))}
    </div>
  );
}

/* ══ อื่น ๆ ════════════════════════════════════════════════ */

export function Spinner({ label }: { label?: string }) {
  return (
    <div className="center-screen">
      <div className="spinner" />
      {label && <div>{label}</div>}
    </div>
  );
}

export function Alert({ tone = 'crit', children }: { tone?: 'crit' | 'warn' | 'ok'; children: ReactNode }) {
  return <div className={`alert ${tone}`} role="alert">{children}</div>;
}

export function Field({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <div className="field">
      <label>{label}</label>
      {children}
    </div>
  );
}

/**
 * ปุ่มที่กันการกดซ้ำระหว่างรอเซิร์ฟเวอร์ตอบ
 *
 * ★ จำเป็นจริง ๆ ไม่ใช่ของประดับ ★ พนักงานกดปุ่ม "ชำระเงิน" สองครั้ง
 * เพราะครั้งแรกไม่มีอะไรเกิดขึ้นทันที — ถ้าไม่กันไว้จะได้บิลซ้ำหรือคืนเงินซ้ำ
 */
export function AsyncButton({
  onClick,
  children,
  className = 'btn',
  disabled = false,
}: {
  onClick: () => Promise<unknown>;
  children: ReactNode;
  className?: string;
  disabled?: boolean;
}) {
  const [busy, setBusy] = useState(false);
  const alive = useRef(true);

  /**
   * ★ ต้องตั้งกลับเป็น true ตอน mount ด้วย ไม่ใช่แค่ตั้ง false ตอน unmount ★
   *
   * StrictMode ของ React จำลองการ unmount แล้ว mount ใหม่ทันทีตอน dev
   * ถ้าตั้งแค่ขาลง ค่าจะค้างเป็น false ตลอดไป แล้ว busy ไม่มีวันถูกปลด
   * ปุ่มจะหมุนค้างหลังกดครั้งแรก — กดครั้งที่สองไม่ได้อีกเลยทั้งกะ
   */
  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; };
  }, []);

  return (
    <button
      type="button"
      className={className}
      disabled={disabled || busy}
      onClick={async () => {
        if (busy) return;
        setBusy(true);
        try {
          await onClick();
        } finally {
          if (alive.current) setBusy(false);
        }
      }}
    >
      {busy ? <span className="spinner" style={{ width: 18, height: 18 }} /> : children}
    </button>
  );
}
